import { useState, useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Navbar from './components/Navbar';
import Footer from '../home/components/Footer';

interface Project {
  id: string;
  title: string;
  category: string;
  description: string;
  imageUrl: string;
  completedDate: string;
}

export default function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.lineflow.com.au';

  useEffect(() => {
    const savedProjects = localStorage.getItem('lineflow-projects');
    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    } else {
      // Default projects
      const defaultProjects: Project[] = [
        {
          id: '1',
          title: 'Riverside Residential Complex',
          category: 'new-home',
          description: 'Complete plumbing installation for 50-unit residential development including water supply, drainage, and gas lines.',
          imageUrl: 'https://public.readdy.ai/ai/img_res/6f72b4d3aafc44b4bd037c460c42d120.jpg',
          completedDate: '2024-01'
        },
        {
          id: '2',
          title: 'Downtown Underground Infrastructure',
          category: 'underground',
          description: 'Major underground sewer and water main installation project for city infrastructure upgrade.',
          imageUrl: 'https://readdy.ai/api/search-image?query=Large%20scale%20underground%20infrastructure%20construction%20deep%20excavation%20with%20sewer%20pipes%20and%20water%20mains%20installation%20heavy%20machinery%20professional%20civil%20engineering%20work%20emphasizing%20expertise%20and%20safety&width=800&height=600&seq=project-002&orientation=landscape',
          completedDate: '2023-11'
        },
        {
          id: '3',
          title: 'Oakwood Estates Development',
          category: 'civil',
          description: 'Civil engineering support for large-scale residential development including site planning and infrastructure coordination.',
          imageUrl: 'https://readdy.ai/api/search-image?query=Large%20residential%20development%20construction%20site%20multiple%20buildings%20under%20construction%20extensive%20civil%20engineering%20infrastructure%20work%20professional%20project%20coordination%20emphasizing%20scale%20and%20organization&width=800&height=600&seq=project-003&orientation=landscape',
          completedDate: '2023-09'
        },
        {
          id: '4',
          title: 'Commercial Plaza Plumbing',
          category: 'commercial',
          description: 'Complete plumbing system for new commercial plaza including retail spaces and office buildings.',
          imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20commercial%20plaza%20construction%20site%20professional%20plumbing%20installation%20for%20retail%20and%20office%20spaces%20organized%20infrastructure%20work%20emphasizing%20commercial%20expertise%20and%20quality&width=800&height=600&seq=project-004&orientation=landscape',
          completedDate: '2023-07'
        },
        {
          id: '5',
          title: 'Hillside New Home Construction',
          category: 'new-home',
          description: 'Custom home plumbing installation featuring advanced water filtration and energy-efficient systems.',
          imageUrl: 'https://readdy.ai/api/search-image?query=Luxury%20custom%20home%20construction%20site%20professional%20plumbing%20rough-in%20work%20modern%20pipe%20systems%20and%20fixtures%20clean%20organized%20installation%20emphasizing%20quality%20and%20attention%20to%20detail&width=800&height=600&seq=project-005&orientation=landscape',
          completedDate: '2023-05'
        },
        {
          id: '6',
          title: 'Metro Area Drainage Project',
          category: 'underground',
          description: 'Comprehensive stormwater drainage system installation for metropolitan area flood prevention.',
          imageUrl: 'https://readdy.ai/api/search-image?query=Metropolitan%20stormwater%20drainage%20construction%20large%20diameter%20pipes%20installation%20professional%20underground%20infrastructure%20work%20heavy%20equipment%20emphasizing%20civil%20engineering%20expertise&width=800&height=600&seq=project-006&orientation=landscape',
          completedDate: '2023-03'
        }
      ];
      setProjects(defaultProjects);
      localStorage.setItem('lineflow-projects', JSON.stringify(defaultProjects));
    }
  }, []);

  useSEO({
    title: 'Our Projects - LineFlow Underground & Civil | Completed Plumbing & Civil Engineering Projects',
    description: 'Explore our portfolio of completed plumbing and civil engineering projects including new home plumbing, underground piping, and commercial installations. Quality workmanship and professional service.',
    keywords: 'plumbing projects, civil engineering projects, completed projects, new home plumbing, underground piping, commercial plumbing, project portfolio',
    canonical: `${siteUrl}/projects`,
    ogType: 'website',
    ogTitle: 'Our Projects - LineFlow Underground & Civil',
    ogDescription: 'Explore our portfolio of completed plumbing and civil engineering projects. Quality workmanship and professional service.',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${siteUrl}/projects#webpage`,
      url: `${siteUrl}/projects`,
      name: 'Our Projects - LineFlow Underground & Civil',
      description: 'Portfolio of completed plumbing and civil engineering projects',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Projects',
            item: `${siteUrl}/projects`
          }
        ]
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: projects.slice(0, 6).map((project, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'CreativeWork',
            name: project.title,
            description: project.description,
            image: project.imageUrl,
            dateCompleted: project.completedDate
          }
        }))
      }
    }
  });

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'new-home', label: 'New Home Plumbing' },
    { id: 'underground', label: 'Underground Piping' },
    { id: 'civil', label: 'Civil Engineering' },
    { id: 'commercial', label: 'Commercial Projects' }
  ];

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0F4C81] to-[#1E88E5] text-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <span className="text-white font-bold text-sm tracking-wide">OUR PORTFOLIO</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-black mb-6">
              Completed Projects
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Explore our portfolio of successful plumbing and civil engineering projects
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-6">
                <i className="ri-folder-open-line text-4xl text-gray-400"></i>
              </div>
              <p className="text-xl text-gray-500">No projects found in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-[#FF6B35]"
                >
                  {/* Image */}
                  <div className="relative w-full h-64 overflow-hidden">
                    <img
                      src={project.imageUrl}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 px-4 py-2 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white text-sm font-bold rounded-full">
                      {project.category}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-[#0F4C81] mb-3 group-hover:text-[#FF6B35] transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {project.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <i className="ri-calendar-line text-[#FF6B35]"></i>
                      <span>Completed: {new Date(project.completedDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long' 
                      })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
